import React, { useState, useEffect } from "react";
import { Box, Snackbar, Alert, Chip } from "@mui/material";
import ListComponent from "../../containers/PageTemplate/ListComponent";
import Form from "../../containers/PageTemplate/Form";
import DeleteModal from "../../containers/PageTemplate/DeleteModal";
import { FaCog, FaPlug, FaFan, FaBatteryFull, FaMicrochip } from "react-icons/fa";
import {
  getRequests,
  getRequestDetails,
  createRequest,
  updateRequest,
  deleteRequest,
  getRequestStatusOptions,
  getEquipmentOptions
} from "../api/requests";
import { getDevices } from "../api/devices"
// Shared parts catalog — a test request must name real catalog parts so its
// results can be associated back to a propulsion combination (motor_id,
// propeller_id, esc_id, battery_id) and feed the measured curve.
import { getCatalog } from "../../../containers/components/propulsionCalc/api/propulsion";

const motorLabel = (m) => `${m.brand} ${m.name}${m.kv ? ` · ${m.kv}Kv` : ""}`;
const propLabel = (p) => `${p.brand ? `${p.brand} ` : ""}${p.name}${p.diameter_in ? ` · ${p.diameter_in}×${p.pitch_in}` : ""}`;
const escLabel = (e) => `${e.brand ? `${e.brand} ` : ""}${e.name}${e.max_current_a ? ` · ${e.max_current_a}A` : ""}`;
const batLabel = (b) => `${b.brand ? `${b.brand} ` : ""}${b.name}${b.capacity_mah ? ` · ${b.capacity_mah}mAh` : ""}`;

const toOptions = (items = [], fmt) =>
  items.map((item) => ({ value: item.id, label: fmt(item) }));

/**
 * Describe a request's part selection. New requests store a catalog `id`, which
 * is resolved to a label here (single source of truth). Requests created before
 * this change stored loose attribute bags, so fall back to those keys rather
 * than rendering a blank row.
 */
const partSummary = (part, options, legacyKeys) => {
  if (!part) return null;
  const qty = part.quantity ? ` (x${part.quantity})` : "";
  if (part.id) {
    const match = options?.find((o) => o.value === part.id);
    return `${match ? match.label : part.id}${qty}`;
  }
  const legacy = legacyKeys.map((k) => part[k]).filter(Boolean).join(" ");
  return legacy ? `${legacy}${qty}` : null;
};

const getStatusColor = (status) => {
  switch (status) {
    case 'pending': return 'warning';
    case 'approved': return 'info';
    case 'rejected': return 'error';
    case 'in-progress': return 'primary';
    case 'shipped': return 'secondary';
    case 'delivered': return 'success';
    default: return 'default';
  }
};

const getStatusLabel = (status) => {
  switch (status) {
    case 'pending': return 'Pending';
    case 'approved': return 'Approved';
    case 'rejected': return 'Rejected';
    case 'in-progress': return 'In Progress';
    case 'shipped': return 'Shipped';
    case 'delivered': return 'Delivered';
    default: return 'Unknown';
  }
};

const Requests = () => {
  const [requests, setRequests] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteModalItemCount, setDeleteModalItemCount] = useState(0);
  const [formObject, setFormObject] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isLoading, setIsLoading] = useState(false);
  const [equipmentOptions, setEquipmentOptions] = useState({});
  const [catalogOptions, setCatalogOptions] = useState(null);
  const [, setStatusOptions] = useState([]);
  const [deviceOptions, setDeviceOptions] = useState([]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoading(true);
        const [requestsData, equipmentOpts, statusOpts, devices] = await Promise.all([
          getRequests(),
          getEquipmentOptions(),
          getRequestStatusOptions(),
          getDevices()
        ]);
        setRequests(requestsData);
        setEquipmentOptions(equipmentOpts);
        setStatusOptions(statusOpts);

        const deviceDropdownOptions = devices.map(device => ({
          label: device.name || `${device.deviceName}`,
          value: device.id
        }));
        setDeviceOptions(deviceDropdownOptions);

        // Catalog is a separate service — if it's unavailable the request form
        // still works, it just falls back to the legacy attribute dropdowns.
        try {
          const catalog = await getCatalog();
          setCatalogOptions({
            motors: toOptions(catalog.motors, motorLabel),
            propellers: toOptions(catalog.propellers, propLabel),
            escs: toOptions(catalog.escs, escLabel),
            batteries: toOptions(catalog.batteries, batLabel),
          });
        } catch (catalogError) {
          console.error("Parts catalog unavailable:", catalogError);
        }
      } catch (error) {
        console.error("Error loading initial data:", error);
        setSnackbar({
          open: true,
          message: 'Failed to load requests data',
          severity: 'error'
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  const handleRefreshClick = async () => {
    try {
      setIsLoading(true);
      const requestsData = await getRequests();
      setRequests(requestsData);
    } catch (error) {
      console.error("Error refreshing requests:", error);
      setSnackbar({
        open: true,
        message: 'Failed to refresh requests',
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddClick = async (newRequestObject) => {
    try {
      setIsLoading(true);
      const createdRequest = await createRequest(newRequestObject);

      setRequests(prev => [...prev, createdRequest.data]);
      
      setSnackbar({
        open: true,
        message: 'Request submitted successfully',
        severity: 'success'
      });
      setShowAddForm(false);
      setFormObject({});
    } catch (error) {
      console.error("Error creating request:", error);
      setSnackbar({
        open: true,
        message: 'Failed to submit request',
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClick = async (editedRequestObject) => {
    try {
      setIsLoading(true);
      const updatedRequest = await updateRequest(editedRequestObject);      
      setRequests(prev => 
        prev.map(req => req.requestId === updatedRequest.data.requestId ? updatedRequest.data : req)
      );
      
      setSnackbar({
        open: true,
        message: 'Request updated successfully',
        severity: 'success'
      });
      setShowEditForm(false);
      setFormObject({});
    } catch (error) {
      console.error("Error updating request:", error);
      setSnackbar({
        open: true,
        message: 'Failed to update request',
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteConfirmed = async () => {
    if (selectedRows.length === 0) return;

    try {
      setIsLoading(true);
      await Promise.all(selectedRows.map(row => deleteRequest(row.requestId)));
      
      setRequests(prev => 
        prev.filter(req => !selectedRows.some(row => row.requestId === req.requestId))
      );
      
      setSnackbar({
        open: true,
        message: `${selectedRows.length} requests deleted successfully`,
        severity: 'success'
      });
      setShowDeleteModal(false);
      setSelectedRows([]);
    } catch (error) {
      console.error("Error deleting requests:", error);
      setSnackbar({
        open: true,
        message: 'Failed to delete requests',
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDeleteModal = (count) => {
    setDeleteModalItemCount(count);
    setShowDeleteModal(true);
  };

  const handleActionSelect = async (action, selectedItems) => {
    const selectedRequests = requests.filter(req => selectedItems.includes(req.id));
    setSelectedRows(selectedRequests);

    if (action === "Modify" && selectedRequests.length === 1) {
      try {
        setIsLoading(true);
        const requestDetails = await getRequestDetails(selectedRequests[0].id);
        setFormObject(prev => ({
          ...prev,
          ...requestDetails
        }));
        setShowEditForm(true);
      } catch (error) {
        console.error("Error fetching request details:", error);
        setSnackbar({
          open: true,
          message: 'Failed to load request details',
          severity: 'error'
        });
      } finally {
        setIsLoading(false);
      }
    } else if (action === "Delete") {
      if (selectedRequests.length > 0) {
        handleOpenDeleteModal(selectedRequests.length);
      }
    }
  };

  const columns = [
    {
      id: "equipmentType",
      label: "Equipment",
      type: "text",
      render: (row) => {
        const equipmentIcons = [];
      
        if (row.motor) {
          equipmentIcons.push(<Box key="motor" sx={{ display: 'flex', alignItems: 'center', mr: 1 }}><FaCog /> &nbsp; Motor</Box>);
        }
        if (row.propeller) {
          equipmentIcons.push(<Box key="propeller" sx={{ display: 'flex', alignItems: 'center', mr: 1 }}><FaFan /> &nbsp; Propeller</Box>);
        }
        if (row.esc) {
          equipmentIcons.push(<Box key="esc" sx={{ display: 'flex', alignItems: 'center', mr: 1 }}><FaPlug /> &nbsp; ESC</Box>);
        }
        if (row.battery) {
          equipmentIcons.push(<Box key="battery" sx={{ display: 'flex', alignItems: 'center', mr: 1 }}><FaBatteryFull /> &nbsp; Battery</Box>);
        }
        if (row.flightController) {
          equipmentIcons.push(<Box key="fc" sx={{ display: 'flex', alignItems: 'center', mr: 1 }}><FaMicrochip /> &nbsp; Flight Controller</Box>);
        }
      
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {equipmentIcons.length > 0 ? equipmentIcons : "N/A"}
          </Box>
        );
      }
    },
    {
      id: "details",
      label: "Details",
      type: "text",
      render: (row) => {
        const details = [];

        const motor = partSummary(row.motor, catalogOptions?.motors, ["kv", "size", "brand"]);
        if (motor) details.push(`Motor: ${motor}`);

        const propeller = partSummary(row.propeller, catalogOptions?.propellers, ["size", "material"]);
        if (propeller) details.push(`Propeller: ${propeller}`);

        const esc = partSummary(row.esc, catalogOptions?.escs, ["amperage", "cells", "protocol"]);
        if (esc) details.push(`ESC: ${esc}`);

        const battery = partSummary(row.battery, catalogOptions?.batteries, ["capacity", "cells", "cRating"]);
        if (battery) details.push(`Battery: ${battery}`);

        const fc = partSummary(row.flightController, null, ["mcu", "gyro"]);
        if (fc) details.push(`FC: ${fc}`);

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {details.length > 0 ? details.map((detail, index) => (
              <Box key={index}>{detail}</Box>
            )) : row.description || "N/A"}
          </Box>
        );
      }
    },
    { id: "requestedFor", label: "Requested For", type: "text",
      render: (row) => {

        if (row.requestedFor === "External Device") {
        return "External Device";
      }
      
      const device = deviceOptions.find(d => d.value === row.requestedFor);
      
      return device ? device.label : `Device (ID: ${row.requestedFor})`;
    }
     },
    {
      id: "status",
      label: "Status",
      type: "text",
      render: (row) => (
        <Chip
          label={getStatusLabel(row.status)}
          color={getStatusColor(row.status)}
          size="small"
          sx={{ fontWeight: "bold" }}
        />
      ),
    },
    { id: "timestamp", label: "Date", type: "date" },
  ];

  const formColumns = [
    {
      id: "requestedFor",
      label: "Request For Device",
      type: "dropdown",
      options: [...deviceOptions,{ label: "External Device", value: "External Device" }],
    },
    {
      id: "description",
      label: "Description",
      type: "description",
      placeholder: "Add a description...",
    },
    // Catalog-backed pickers: the selected value is the catalog part id, which
    // is what lets a completed run be associated with a propulsion combination.
    // If the catalog didn't load, fall back to the legacy attribute dropdowns.
    {
      id: "motor",
      label: "Motor",
      type: "group",
      children: catalogOptions
        ? [
            { id: "motor.id", label: "Motor", type: "dropdown", options: catalogOptions.motors },
            { id: "motor.quantity", label: "Quantity", type: "number" },
          ]
        : [
            { id: "motor.kv", label: "KV", type: "dropdown", options: equipmentOptions.motor?.kv || [] },
            { id: "motor.size", label: "Size", type: "dropdown", options: equipmentOptions.motor?.size || [] },
            { id: "motor.brand", label: "Brand", type: "dropdown", options: equipmentOptions.motor?.brand || [] },
            { id: "motor.quantity", label: "Quantity", type: "number" },
          ],
    },
    {
      id: "propeller",
      label: "Propeller",
      type: "group",
      children: catalogOptions
        ? [
            { id: "propeller.id", label: "Propeller", type: "dropdown", options: catalogOptions.propellers },
            { id: "propeller.quantity", label: "Quantity", type: "number" },
          ]
        : [
            { id: "propeller.size", label: "Size", type: "dropdown", options: equipmentOptions.propeller?.size || [] },
            { id: "propeller.material", label: "Material", type: "dropdown", options: equipmentOptions.propeller?.material || [] },
            { id: "propeller.quantity", label: "Quantity", type: "number" },
          ],
    },
    {
      id: "esc",
      label: "ESC",
      type: "group",
      children: catalogOptions
        ? [
            { id: "esc.id", label: "ESC", type: "dropdown", options: catalogOptions.escs },
            { id: "esc.quantity", label: "Quantity", type: "number" },
          ]
        : [
            { id: "esc.amperage", label: "Amperage", type: "dropdown", options: equipmentOptions.esc?.amperage || [] },
            { id: "esc.cells", label: "Cells", type: "dropdown", options: equipmentOptions.esc?.cells || [] },
            { id: "esc.protocol", label: "Protocol", type: "dropdown", options: equipmentOptions.esc?.protocol || [] },
            { id: "esc.quantity", label: "Quantity", type: "number" },
          ],
    },
    {
      id: "battery",
      label: "Battery",
      type: "group",
      children: catalogOptions
        ? [
            { id: "battery.id", label: "Battery", type: "dropdown", options: catalogOptions.batteries },
            { id: "battery.quantity", label: "Quantity", type: "number" },
          ]
        : [
            { id: "battery.capacity", label: "Capacity", type: "dropdown", options: equipmentOptions.battery?.capacity || [] },
            { id: "battery.cells", label: "Cells", type: "dropdown", options: equipmentOptions.battery?.cells || [] },
            { id: "battery.cRating", label: "C-Rating", type: "dropdown", options: equipmentOptions.battery?.cRating || [] },
            { id: "battery.quantity", label: "Quantity", type: "number" },
          ],
    },
    {
      id: "flightController",
      label: "Flight Controller Configuration",
      type: "group",
      children: [
        { id: "flightController.mcu", label: "MCU", type: "dropdown", options: equipmentOptions.flightController?.mcu || [] },
        { id: "flightController.gyro", label: "Gyro", type: "dropdown", options: equipmentOptions.flightController?.gyro || [] },
        { id: "flightController.quantity", label: "Quantity", type: "number" },
      ],
    },
  ];

  return (
    <Box
      sx={{
        padding: 2,
        boxSizing: "border-box",
        height: "100%",
        position: "relative",
      }}
    >
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%", color: "white" }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      <ListComponent
        title="My Requests"
        columns={columns}
        showAddButton={true}
        addButtonText="Add New Request"
        showActions={true}
        actionOptions={[
          { value: "Modify", label: "Modify" },
          { value: "Delete", label: "Delete" },
        ]}
        data={requests}
        onRefresh={handleRefreshClick}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        setShowAddForm={setShowAddForm}
        setShowUpdateForm={setShowEditForm}
        onOptions={handleActionSelect}
        onDeleteClick={handleOpenDeleteModal}
        formObject={formObject}
        setFormObject={setFormObject}
        object="request"
        isLoading={isLoading}
      />

      {/* Add Request Form */}
      {showAddForm && (
        <Form
          formTitle="Add New Request"
          object="request"
          columns={formColumns}
          formObject={formObject}
          setFormObject={setFormObject}
          setShowForm={setShowAddForm}
          submitButtonText="Add"
          onSubmit={() => handleAddClick(formObject)}
          isLoading={isLoading}
        />
      )}

      {/* Edit Request Form */}
      {showEditForm && (
        <Form
          formTitle="Edit Request"
          object="request"
          columns={formColumns}
          formObject={formObject}
          setFormObject={setFormObject}
          setShowForm={setShowEditForm}
          submitButtonText="Save Changes"
          onSubmit={() => handleEditClick(formObject)}
          isLoading={isLoading}
        />
      )}

      <DeleteModal
        open={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        handleDelete={handleDeleteConfirmed}
        item={`${deleteModalItemCount} request${deleteModalItemCount > 1 ? "s" : ""}`}
        isLoading={isLoading}
      />
    </Box>
  );
};

export default Requests;